import Button, { ButtonProps } from '@carvana/ds-button';
import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import cx from 'classnames';
import React from 'react';
import PrimaryCtaType from '~/constants/PrimaryCtaType';
import SecondaryCtaType from '~/constants/SecondaryCtaType';
import { usePrimaryButtonHandler } from '~/hooks/vdp/cta';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import useSecondaryCtaButtonState from '~/hooks/vdp/selectors/useSecondaryCtaButtonState';

const dummyFunction = () => {};

type OwnProps<T> = {
  ButtonComponent?: React.ComponentType<any>;
  className?: string;
  'data-analytics-id': string | null;
  'data-analytics-attributes'?: AnalyticsAttributes;
  'data-testid'?: string;
  getStartedText?: string;
  onClick?: () => void;
} & T;

export default function PrimaryCtaButton<T = ButtonProps>({
  ButtonComponent = Button,
  className = '',
  'data-analytics-id': analyticsId,
  'data-analytics-attributes': analyticsAttributes,
  'data-testid': testId,
  getStartedText = 'Get started',
  onClick = dummyFunction,
  ...rest
}: OwnProps<T>) {
  const handleOnClick = usePrimaryButtonHandler(onClick) ?? dummyFunction;
  const { isDisabled, isLoading, type } = usePrimaryCtaButtonState();
  const { type: secondaryType } = useSecondaryCtaButtonState();

  const primaryCtaTypes: Record<PrimaryCtaType, string | null> = {
    [PrimaryCtaType.CancelPreOrder]: null,
    [PrimaryCtaType.ContinuePurchase]: 'Continue purchase',
    [PrimaryCtaType.Default]: getStartedText,
    [PrimaryCtaType.DisabledGetStarted]: getStartedText,
    [PrimaryCtaType.DisabledPreOrder]: 'Pre-order now',
    [PrimaryCtaType.GetAlerts]: 'Get alerts',
    [PrimaryCtaType.GetStarted]: getStartedText,
    [PrimaryCtaType.NotifyMe]: 'Notify me',
    [PrimaryCtaType.Reservable]: 'Pre-order now',
    [PrimaryCtaType.Reservation]: null,
    [PrimaryCtaType.ViewSimilarVehicles]:
      secondaryType === SecondaryCtaType.Default
        ? 'View similar vehicles'
        : 'View similar'
  };

  return primaryCtaTypes[type] ? (
    <ButtonComponent
      className={cx(className, {
        '!bg-[#00874a] hover:!bg-[var(--reference-green-600)] active:!bg-[#0a4729]':
          !isDisabled
      })}
      disabled={isDisabled}
      data-adobe-id={null}
      data-analytics-id={analyticsId}
      data-analytics-attributes={JSON.stringify({
        ...analyticsAttributes,
        description: 'primary cta',
        text: primaryCtaTypes[type]
      })}
      data-testid={testId}
      isLoading={isLoading}
      onClick={handleOnClick}
      variant="primary"
      {...rest}
    >
      {primaryCtaTypes[type]}
    </ButtonComponent>
  ) : null;
}
